import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { subscribersApi } from "../utils/api";
import { GreenSwitch } from "./ui/green-toggle";

interface Subscriber {
  _id: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  is_active?: boolean;
}

interface SubscriberListResponse {
  docs: Subscriber[];
  totalDocs: number;
  totalPages: number;
}

function formatDateTime12h(input: string) {
  const date = new Date(input);
  const datePart = date.toLocaleDateString();
  const timePart = date
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
    .toUpperCase();
  return `${datePart} ${timePart}`;
}

export function Subscribers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeStates, setActiveStates] = useState<Record<string, boolean>>({});
  const [toggleLoadingIds, setToggleLoadingIds] = useState<Set<string>>(
    new Set()
  );

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await subscribersApi.getSubscribers(
        currentPage,
        limit,
        searchTerm
      );
      const data = response?.data as SubscriberListResponse | undefined;
      if (data && Array.isArray(data.docs)) {
        setSubscribers(data.docs);
        const initActive: Record<string, boolean> = {};
        data.docs.forEach((subscriber) => {
          initActive[subscriber._id] = subscriber.is_active ?? false;
        });
        setActiveStates(initActive);
        setTotalPages(data.totalPages || 0);
        setTotal(data.totalDocs || 0);
      } else {
        setSubscribers([]);
        setActiveStates({});
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      setSubscribers([]);
      setActiveStates({});
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, [currentPage, limit, searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value: string) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handleToggleActive = async (subscriber: Subscriber) => {
    const currentActive = !!activeStates[subscriber._id];
    const nextActive = !currentActive;
    const status = nextActive ? "activated" : "deactivated";
    setToggleLoadingIds((prev) => new Set(prev).add(subscriber._id));
    setActiveStates((prev) => ({
      ...prev,
      [subscriber._id]: nextActive,
    }));

    try {
      const response = await subscribersApi.updateSubscriberActiveStatus(
        subscriber._id,
        status
      );
      if (response && response.statusCode === 200) {
        toast.success(
          response?.message || "Subscriber status updated successfully"
        );
        fetchSubscribers();
      } else {
        setActiveStates((prev) => ({
          ...prev,
          [subscriber._id]: currentActive,
        }));
        toast.error(response?.message || "Failed to update subscriber status");
      }
    } catch (error: any) {
      setActiveStates((prev) => ({
        ...prev,
        [subscriber._id]: currentActive,
      }));
      toast.error(error?.message || "An error occurred");
    } finally {
      setToggleLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(subscriber._id);
        return next;
      });
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Subscribed Emails</h1>
        <p className="text-muted-foreground">
          View and search email subscribers
        </p>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Subscribers ({total})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 border border-border shadow-sm"
              />
            </div>

            <Select value={limit.toString()} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-full sm:w-32 border border-border shadow-sm">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading subscribers...</div>
            </div>
          ) : subscribers.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No subscribers found</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Updated At</TableHead>
                    <TableHead>Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((subscriber) => (
                    <TableRow key={subscriber._id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {subscriber.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            subscriber.is_active === true
                              ? "bg-green-100 text-green-800"
                              : subscriber.is_active === false
                              ? "bg-gray-100 text-gray-700"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {subscriber.is_active === true
                            ? "ACTIVE"
                            : subscriber.is_active === false
                            ? "INACTIVE"
                            : "UNKNOWN"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDateTime12h(subscriber.createdAt)}
                      </TableCell>
                      <TableCell>
                        {formatDateTime12h(subscriber.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <GreenSwitch
                          checked={!!activeStates[subscriber._id]}
                          onChange={() => handleToggleActive(subscriber)}
                          aria-label="Toggle subscriber active status"
                          disabled={toggleLoadingIds.has(subscriber._id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
